<template>
  <div id="app">
    <router-view v-if="serviceSDK.sdkStatus === 'initialized'" />
  </div>
</template>

<script>
import ServiceSDKModule from "@gtt/service-sdk";
import * as KEYS from "@/router/routerKeys";
import LoadingManager from "@/utils/LoadingManager";
import { mapGetters, mapMutations } from "vuex";
import { initWebRtcSdkListener } from "@/sdkMethods/webRtcSdk";
import { Config } from "./sdkMethods/config";
import { getSubscriptionList, logoutAccount } from "./sdkMethods/account";
import { getLangCode } from "./utils/formatType";
import ImageUtils from "./utils/image";
import LoginUtils from "./utils/login";

const LogLevel =
  ServiceSDKModule.tech.gtt.frontapp.servicesdk.interfaces.base.LogLevel;

class MySDKConfig extends ServiceSDKModule.tech.gtt.frontapp.servicesdk
  .interfaces.base.SDKConfiguration {
  constructor(lang) {
    super();
    this.lang = lang;
  }
  entryServerUrl() {
    return window.ENV.entryServerUrl;
  }
  shareKey() {
    return window.ENV.shareKey;
  }
  appId() {
    return window.ENV.appId;
  }
  brandId() {
    return window.ENV.brandId;
  }
  sourceId() {
    return "30006401-31002837";
  }
  packageSeq() {
    return "1";
  }
  pluginVersionCode() {
    return "13";
  }
  appVersionCode() {
    return "1000";
  }
  appVersionName() {
    return "v1.0.0";
  }
  storageKey() {
    return super.storageKey();
  }
  isSDKDebuggable() {
    return false;
  }
  appLanguage() {
    return getLangCode(this.lang);
  }
  clientType() {
    return ServiceSDKModule.tech.gtt.frontapp.servicesdk.interfaces.base
      .SDKConfiguration.ClientType.WEB;
  }
  ijmKey() {
    return super.ijmKey();
  }
  st() {
    return null;
  }
  extraParams() {
    return ServiceSDKModule.kotlin.collections.KtMap.fromJsMap(new Map());
  }
  extraHeaderParams() {
    return ServiceSDKModule.kotlin.collections.KtMap.fromJsMap(new Map());
  }
  e2eeEnabled() {
    return true;
  }
  depth() {
    return 4;
  }
  dataBoostEnabled() {
    return false;
  }
  logLevel() {
    return LogLevel.Error;
  }
}

export default {
  name: "app",
  data() {
    return {
      serviceSDK: {
        sdkContent: null,
        sdkStatus: "idle", // idle, initializing, initialized, error
        sdkErrors: null,
        loginStatus: null,
        authRequired: false,
      },
      accountListenerDispose: null,
      notificationListenerDispose: null,
    };
  },
  provide() {
    return {
      serviceSDK: this.serviceSDK,
    };
  },
  computed: {
    ...mapGetters(["programSubscribeList", "activeLanguage"]),
  },
  async created() {
    this.initializeSDK();
    initWebRtcSdkListener();
  },
  beforeDestroy() {
    if (this.accountListenerDispose) {
      this.accountListenerDispose.dispose();
    }
    if (this.notificationListenerDispose) {
      this.notificationListenerDispose.dispose();
    }
  },
  methods: {
    ...mapMutations(["set_program_notify_list"]),
    async initializeSDK() {
      LoadingManager.showLoading();
      this.serviceSDK.sdkStatus = "idle";
      this.serviceSDK.sdkErrors = null;
      this.serviceSDK.sdkContent = null;
      this.serviceSDK.loginStatus = false;
      this.serviceSDK.authRequired = false;
      try {
        console.log("initializeSDK:", this.serviceSDK.sdkStatus);
        this.serviceSDK.sdkStatus = "initializing";
        const sdkInstance = await this.initializeActualSDK((error) => {
          this.sdkErrors = error;
        });
        this.serviceSDK.sdkContent = sdkInstance;
        this.serviceSDK.sdkStatus = "initialized";
        await LoginUtils.checkLoginStatus(this.serviceSDK);
        await this.decryptImage();
        this.initAccountListener();
        this.initProgramSubscribeListener();
        LoadingManager.hideLoading();
      } catch (exception) {
        LoadingManager.hideLoading();
        this.serviceSDK.sdkStatus = "error";
        this.serviceSDK.sdkContent = null;
        this.serviceSDK.loginStatus = false;
        this.$message.error(exception?.message || "Unknown error");
      }
    },
    async initializeActualSDK(setErrorCallback) {
      return new Promise((resolve, reject) => {
        try {
          const config = new MySDKConfig(this.activeLanguage);
          const builder =
            new ServiceSDKModule.tech.gtt.frontapp.servicesdk.interfaces.ServiceSDK.Builder(
              config
            );
          const initCallback = {
            call(result) {
              if (result.isSuccess()) {
                setErrorCallback(null);
                resolve(sdkInstance);
              } else {
                const sdkError = result.getError();
                const errorMessage = sdkError
                  ? `Error Code: ${sdkError.errorCode}`
                  : "Unknown error";
                const error =
                  sdkError instanceof Error
                    ? sdkError
                    : new Error(errorMessage);
                setErrorCallback(error);
                reject(error);
              }
            },
          };
          builder.setCallback(initCallback);
          const sdkInstance = builder.build();
        } catch (err) {
          const error =
            err instanceof Error ? err : new Error("SDK setup failed");
          setErrorCallback(error);
          reject(error);
        }
      });
    },
    // 图片解密
    async decryptImage() {
      ImageUtils.decryptImage(window.logo, this.serviceSDK.sdkContent).then((logo) => {
        this.$store.commit("set_logo", logo);
        console.log("logo", logo);
      });
    },
    // 注册账号事件监听
    initAccountListener() {
      const accountService = this.serviceSDK.sdkContent.getAccountService();
      const AccountEventType =
        ServiceSDKModule.tech.gtt.frontapp.servicesdk.interfaces.service.account
          .AccountEventType;
      this.accountListenerDispose = accountService.registerAccountEvent({
        call: async (result) => {
          // 监听踢出事件
          if (result.event === AccountEventType.ACCOUNT_KICK_OFF) {
            this.$confirm(this.$t("account_kicked_off_message"), {
              customClass: "logout_confirm_dialog",
              confirmButtonText: this.$t("exit"),
              cancelButtonClass: "global_primary_btn",
              center: true,
              showClose: false,
              closeOnClickModal: false,
              closeOnPressEscape: false,
              closeOnHashChange: false,
              showCancelButton: false,
            })
              .then(() => {
                // this.serviceSDK.sdkContent.getAccountService().logout().proceedWithCallback();
                logoutAccount(this.serviceSDK.sdkContent);
                this.$router.push({
                  name: KEYS.LOGIN,
                });
              });
          }
          // 监听token失效事件/登出
          if (
            result.event === AccountEventType.TOKEN_EXPIRE ||
            result.event === AccountEventType.LOGOUT_SUCCESS
          ) {
            this.serviceSDK.loginStatus = false;
            this.serviceSDK.authRequired = true;
            this.$router.push({
              name: KEYS.LOGIN,
            });
          }
          // 监听登陆成功事件
          if (
            result.event === AccountEventType.LOGIN_SUCCESS ||
            result.event === AccountEventType.REGISTER_SUCCESS
          ) {
            this.serviceSDK.loginStatus = true;
            this.serviceSDK.authRequired = false;
            await LoginUtils.handleAfterLoginPage(this.serviceSDK);
          }
        },
      });
    },
    // 注册预定节目监听
    initProgramSubscribeListener() {
      const ProfileEventType =
        ServiceSDKModule.tech.gtt.frontapp.servicesdk.interfaces.service.profile
          .ProfileEventType;
      this.programSubscribeListenerDispose = this.serviceSDK.sdkContent
        .getProfileService()
        .getLiveProfile()
        .registerProgramSubscribeEvent({
          call: (result) => {
            console.log("注册预定节目监听", result);
            if (result.event === ProfileEventType.SUBSCRIBE_UPDATE) {
              console.log("更新预定节目列表 result", result);
              // 更新预定节目列表
              const subscribeList = [...this.programSubscribeList];
              subscribeList.push(result.data);
              this.set_program_notify_list(subscribeList);
            }
          },
        });
    },
  },
};
</script>

<style scoped lang="scss"></style>
